import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../utils/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { toast } from 'sonner';
import { Loader2, Search, Type, DollarSign } from 'lucide-react';
import { fmtMoney, parseMontoAR } from './formato';

const TANDA = 50;

const Opcion = ({ activa, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors ${
      activa ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
    }`}
  >
    {children}
  </button>
);

const hoyMenos = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const BuscarMovimientosDialog = ({ open, onOpenChange, ids, onCreada }) => {
  const [tipo, setTipo] = useState('TEXTO');
  const [texto, setTexto] = useState('');
  const [montoTxt, setMontoTxt] = useState('');
  const [modoMonto, setModoMonto] = useState('EXACTO');
  const [tolerancia, setTolerancia] = useState('1');
  const [dias, setDias] = useState('30');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => { if (open) setEnviando(false); }, [open]);

  const monto = parseMontoAR(montoTxt);
  const tol = parseFloat(String(tolerancia).replace(',', '.'));
  const diasNum = parseInt(dias, 10);
  const tandas = Math.ceil(ids.length / TANDA);
  const minutos = Math.max(1, Math.round((ids.length * 0.5) / 60));

  const error = useMemo(() => {
    if (!ids.length) return 'Seleccioná al menos una billetera';
    if (tipo === 'TEXTO' && texto.trim().length < 2) return 'Escribí al menos 2 caracteres';
    if (tipo === 'MONTO' && !monto) return 'Ingresá un monto válido';
    if (tipo === 'MONTO' && modoMonto === 'APROX' && !(tol > 0 && tol <= 20)) return 'La tolerancia va de 0,1% a 20%';
    if (!(diasNum >= 1 && diasNum <= 60)) return 'El período va de 1 a 60 días';
    return null;
  }, [ids.length, tipo, texto, monto, modoMonto, tol, diasNum]);

  const buscar = async () => {
    if (error) return;
    setEnviando(true);
    try {
      const r = await api.post('/reportes/saldos/busqueda', {
        ids, tipo, dias: diasNum,
        texto: tipo === 'TEXTO' ? texto.trim() : null,
        monto: tipo === 'MONTO' ? monto : null,
        modo_monto: modoMonto,
        tolerancia_pct: modoMonto === 'APROX' ? tol : 1,
      });
      toast.success(`Búsqueda iniciada: ${r.data.total} billeteras en ${r.data.total_tandas} tanda(s)`);
      onCreada(r.data.id);
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo iniciar la búsqueda');
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="buscar-movs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Search size={18} className="text-blue-600" /> Buscar en movimientos</DialogTitle>
          <DialogDescription>Revisa todos los movimientos de cada billetera seleccionada y te dice en cuáles aparece y cuántas veces.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <Opcion activa={tipo === 'TEXTO'} onClick={() => setTipo('TEXTO')} testid="buscar-movs-tipo-texto"><Type size={14} /> Palabra o frase</Opcion>
            <Opcion activa={tipo === 'MONTO'} onClick={() => setTipo('MONTO')} testid="buscar-movs-tipo-monto"><DollarSign size={14} /> Monto</Opcion>
          </div>

          {tipo === 'TEXTO' ? (
            <div>
              <Input autoFocus value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Ej: Movimiento revertido" data-testid="buscar-movs-texto-input" />
              <p className="text-xs text-slate-500 mt-1">Busca en la descripción del movimiento, sin importar mayúsculas ni acentos.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <Input autoFocus value={montoTxt} onChange={(e) => setMontoTxt(e.target.value)} placeholder="Ej: 15.000 o 15000,50" data-testid="buscar-movs-monto-input" />
              <div className="flex gap-2">
                <Opcion activa={modoMonto === 'EXACTO'} onClick={() => setModoMonto('EXACTO')} testid="buscar-movs-modo-exacto">Exacto</Opcion>
                <Opcion activa={modoMonto === 'APROX'} onClick={() => setModoMonto('APROX')} testid="buscar-movs-modo-aprox">Aproximado</Opcion>
              </div>
              {modoMonto === 'APROX' && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  Tolerancia ±
                  <Input value={tolerancia} onChange={(e) => setTolerancia(e.target.value)} className="w-20 h-8" data-testid="buscar-movs-tolerancia-input" /> %
                </div>
              )}
              {monto > 0 && (
                <p className="text-xs text-slate-500" data-testid="buscar-movs-rango-monto">
                  {modoMonto === 'EXACTO'
                    ? <>Busca movimientos de exactamente {fmtMoney(monto)} (entrada o salida).</>
                    : <>Busca movimientos entre {fmtMoney(monto * (1 - (tol || 0) / 100))} y {fmtMoney(monto * (1 + (tol || 0) / 100))} (entrada o salida).</>}
                </p>
              )}
            </div>
          )}

          <div>
            <div className="flex items-center gap-2 text-sm text-slate-700">
              Período: últimos
              <Input type="number" min={1} max={60} value={dias} onChange={(e) => setDias(e.target.value)} className="w-20 h-8" data-testid="buscar-movs-dias-input" />
              días
              <div className="flex gap-1 ml-auto">
                {[15, 30, 60].map(n => (
                  <Button key={n} type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setDias(String(n))} data-testid={`buscar-movs-dias-${n}`}>{n}d</Button>
                ))}
              </div>
            </div>
            {diasNum >= 1 && diasNum <= 60 && <p className="text-xs text-slate-500 mt-1">Del {hoyMenos(diasNum)} a hoy. Máximo 60 días.</p>}
          </div>

          <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="buscar-movs-resumen">
            Se van a revisar <b>{ids.length}</b> billeteras en <b>{tandas}</b> tanda(s) de {TANDA}. Los resultados aparecen a medida que termina cada billetera
            {ids.length > 100 && <> (tiempo estimado: ~{minutos} min)</>}.
          </div>

          {error && <p className="text-xs text-red-600" data-testid="buscar-movs-error">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="buscar-movs-cancelar-btn">Cancelar</Button>
            <Button onClick={buscar} disabled={!!error || enviando} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="buscar-movs-confirmar-btn">
              {enviando ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Search size={14} className="mr-1.5" />} Buscar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BuscarMovimientosDialog;
